import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPinIcon, StarIcon, UsersIcon, ClockIcon, PhoneIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, SparklesIcon, UtensilsIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { restaurants } from '../data/restaurants';
export function RestaurantDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedDay, setSelectedDay] = useState(0);
  // Booking widget state
  const [bookingType, setBookingType] = useState<'regular' | 'promotional'>('promotional');
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split('T')[0];
  const [bookingData, setBookingData] = useState({
    date: defaultDate,
    pax: 2,
    time: ''
  });
  const restaurant = restaurants.find(r => r.id === id);
  if (!restaurant) {
    return <div className="w-full min-h-screen bg-gray-50" data-id="element-641">
        <Header data-id="element-642" />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center" data-id="element-643">
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-644">
            Restaurant Not Found
          </h1>
          <Link to="/" className="text-amber-600 hover:text-amber-700 font-semibold" data-id="element-645">
            ← Back to restaurants
          </Link>
        </div>
        <Footer data-id="element-646" />
      </div>;
  }
  // Generate time slots based on booking type
  const generateTimeSlots = (type: 'regular' | 'promotional') => {
    if (type === 'promotional') {
      return ['18:00', '18:15', '18:30', '18:45', '19:00'];
    } else {
      return ['12:00', '12:30', '13:00', '13:30', '14:00', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'];
    }
  };
  const availableTimeSlots = generateTimeSlots(bookingType);
  const handleBookNow = () => {
    // Navigate to booking page with pre-filled data
    const params = new URLSearchParams({
      type: bookingType,
      date: bookingData.date,
      pax: bookingData.pax.toString(),
      time: bookingData.time
    });
    navigate(`/booking/${restaurant.id}?${params.toString()}`);
  };
  const menu = restaurant.menus.daily[selectedDay];
  const totalAvailableSeats = restaurant.tables.reduce((sum, table) => sum + table.available, 0);
  const handlePreviousDay = () => {
    if (selectedDay > 0) {
      setSelectedDay(selectedDay - 1);
    }
  };
  const handleNextDay = () => {
    if (selectedDay < restaurant.menus.daily.length - 1) {
      setSelectedDay(selectedDay + 1);
    }
  };
  const hasPromotionalPackage = restaurant.specialPackages.includes('Ramadan Iftar Special');
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-647">
      <Header data-id="element-648" />
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-649">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 mb-6 font-medium transition-colors" data-id="element-650">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-651" />
          <span className="tracking-wide" data-id="element-652">Back to restaurants</span>
        </Link>
        {/* Image Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-653">
          <div className="md:col-span-2" data-id="element-654">
            <img src={restaurant.images[selectedImage]} alt={restaurant.name} className="w-full h-96 object-cover rounded-2xl shadow-xl" data-id="element-655" />
          </div>
          <div className="flex md:flex-col gap-4" data-id="element-656">
            {restaurant.images.slice(1).map((img, idx) => <img key={idx} src={img} alt={`${restaurant.name} ${idx + 2}`} onClick={() => setSelectedImage(idx + 1)} className="w-full h-44 object-cover rounded-2xl cursor-pointer hover:opacity-80 transition-all shadow-lg hover:shadow-xl" data-id="element-657" />)}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-658">
          {/* Main Content */}
          <div className="lg:col-span-2" data-id="element-659">
            {/* Restaurant Info */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6" data-id="element-660">
              <h1 className="text-5xl font-bold text-gray-900 mb-3 tracking-tight" data-id="element-661">
                {restaurant.name}
              </h1>
              <div className="flex items-center gap-4 mb-5" data-id="element-662">
                <div className="flex items-center gap-1.5 text-amber-600" data-id="element-663">
                  <StarIcon className="w-5 h-5 fill-current" data-id="element-664" />
                  <span className="font-bold text-lg" data-id="element-665">{restaurant.rating}</span>
                  <span className="text-gray-500 text-sm font-medium" data-id="element-666">
                    ({restaurant.reviews} reviews)
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-700" data-id="element-667">
                  <MapPinIcon className="w-5 h-5" data-id="element-668" />
                  <span className="font-medium" data-id="element-669">{restaurant.location}</span>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-5 text-lg" data-id="element-670">
                {restaurant.description}
              </p>
              <div className="flex flex-wrap gap-2 mb-5" data-id="element-671">
                {restaurant.amenities.map((amenity, idx) => <span key={idx} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium" data-id="element-672">
                    {amenity}
                  </span>)}
              </div>
              <div className="grid grid-cols-2 gap-6 pt-5 border-t border-gray-200" data-id="element-673">
                <div className="flex items-center gap-3 text-gray-700" data-id="element-674">
                  <ClockIcon className="w-6 h-6 text-amber-600" data-id="element-675" />
                  <div data-id="element-676">
                    <p className="text-sm text-gray-500 font-medium" data-id="element-677">
                      Iftar Time
                    </p>
                    <p className="font-bold text-lg" data-id="element-678">{restaurant.iftarTime}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-gray-700" data-id="element-679">
                  <PhoneIcon className="w-6 h-6 text-amber-600" data-id="element-680" />
                  <div data-id="element-681">
                    <p className="text-sm text-gray-500 font-medium" data-id="element-682">Contact</p>
                    <p className="font-bold text-lg" data-id="element-683">{restaurant.phone}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Promotion */}
            {restaurant.promotion && <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white mb-6" data-id="element-684">
                <h2 className="text-3xl font-bold mb-3 tracking-tight" data-id="element-685">
                  {restaurant.promotion.title}
                </h2>
                <p className="mb-4 text-lg leading-relaxed" data-id="element-686">
                  {restaurant.promotion.description}
                </p>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4" data-id="element-687">
                  <p className="font-bold text-lg" data-id="element-688">
                    {restaurant.promotion.luckyDraw}
                  </p>
                </div>
              </div>}
            {/* Menu with Day Selector */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6" data-id="element-689">
              <div className="mb-8" data-id="element-690">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-691">
                  Weekly Iftar Menu
                </h2>
                {/* Week Day Selector */}
                <div className="flex items-center gap-4 mb-8" data-id="element-692">
                  <button onClick={handlePreviousDay} disabled={selectedDay === 0} className={`p-2.5 rounded-xl transition-all ${selectedDay === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-amber-600 hover:bg-amber-50'}`} data-id="element-693">
                    <ChevronLeftIcon className="w-6 h-6" data-id="element-694" />
                  </button>
                  <div className="flex-1 overflow-x-auto" data-id="element-695">
                    <div className="flex gap-3" data-id="element-696">
                      {restaurant.menus.daily.map((dayMenu, idx) => <button key={dayMenu.day} onClick={() => setSelectedDay(idx)} className={`flex-shrink-0 px-5 py-3.5 rounded-xl transition-all ${selectedDay === idx ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-xl transform scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-697">
                          <div className="text-center" data-id="element-698">
                            <p className="text-xs font-bold uppercase tracking-wider opacity-90" data-id="element-699">
                              {dayMenu.dayOfWeek}
                            </p>
                            <p className="font-bold text-base mt-1" data-id="element-700">
                              {dayMenu.date.split(',')[0]}
                            </p>
                          </div>
                        </button>)}
                    </div>
                  </div>
                  <button onClick={handleNextDay} disabled={selectedDay === restaurant.menus.daily.length - 1} className={`p-2.5 rounded-xl transition-all ${selectedDay === restaurant.menus.daily.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-amber-600 hover:bg-amber-50'}`} data-id="element-701">
                    <ChevronRightIcon className="w-6 h-6" data-id="element-702" />
                  </button>
                </div>
                {/* Selected Date Display */}
                <div className="flex items-center justify-between mb-8 pb-5 border-b border-gray-200" data-id="element-703">
                  <div className="flex items-center gap-3" data-id="element-704">
                    <CalendarIcon className="w-6 h-6 text-amber-600" data-id="element-705" />
                    <div data-id="element-706">
                      <p className="text-sm text-gray-500 font-medium" data-id="element-707">
                        Viewing menu for
                      </p>
                      <p className="font-bold text-gray-900 text-lg" data-id="element-708">
                        {menu.dayOfWeek}, {menu.date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-709">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-710">
                      Day {menu.day} of Ramadan
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-711">
                <div data-id="element-712">
                  <h3 className="text-xl font-bold text-amber-600 mb-4 tracking-tight" data-id="element-713">
                    To Break Your Fast
                  </h3>
                  <ul className="space-y-2.5" data-id="element-714">
                    {menu.starters.map((item, idx) => <li key={idx} className="flex items-center gap-3 text-gray-700 text-base" data-id="element-715">
                        <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" data-id="element-716"></span>
                        <span className="font-medium" data-id="element-717">{item}</span>
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-718">
                  <h3 className="text-xl font-bold text-amber-600 mb-4 tracking-tight" data-id="element-719">
                    Main Course
                  </h3>
                  <ul className="space-y-2.5" data-id="element-720">
                    {menu.mainCourse.map((item, idx) => <li key={idx} className="flex items-center gap-3 text-gray-700 text-base" data-id="element-721">
                        <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" data-id="element-722"></span>
                        <span className="font-medium" data-id="element-723">{item}</span>
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-724">
                  <h3 className="text-xl font-bold text-amber-600 mb-4 tracking-tight" data-id="element-725">
                    Traditional Short Eats
                  </h3>
                  <ul className="space-y-2.5" data-id="element-726">
                    {menu.shortEats.map((item, idx) => <li key={idx} className="flex items-center gap-3 text-gray-700 text-base" data-id="element-727">
                        <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" data-id="element-728"></span>
                        <span className="font-medium" data-id="element-729">{item}</span>
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-730">
                  <h3 className="text-xl font-bold text-amber-600 mb-4 tracking-tight" data-id="element-731">
                    Desserts & Sweets
                  </h3>
                  <ul className="space-y-2.5" data-id="element-732">
                    {menu.desserts.map((item, idx) => <li key={idx} className="flex items-center gap-3 text-gray-700 text-base" data-id="element-733">
                        <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" data-id="element-734"></span>
                        <span className="font-medium" data-id="element-735">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-736">
                <h3 className="text-xl font-bold text-amber-600 mb-4 tracking-tight" data-id="element-737">
                  Beverages
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-738">
                  {menu.beverages.map((item, idx) => <div key={idx} className="flex items-center gap-2 text-gray-700 text-sm" data-id="element-739">
                      <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" data-id="element-740"></span>
                      <span className="font-medium" data-id="element-741">{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Available Tables */}
            <div className="bg-white rounded-2xl shadow-lg p-8" data-id="element-742">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-743">
                Available Tables
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-744">
                {restaurant.tables.map(table => <div key={table.id} className="border-2 border-gray-200 rounded-xl p-5 text-center hover:border-amber-300 transition-colors" data-id="element-745">
                    <UsersIcon className="w-7 h-7 mx-auto mb-3 text-amber-600" data-id="element-746" />
                    <p className="font-bold text-gray-900 text-lg" data-id="element-747">
                      {table.seats} Seater
                    </p>
                    <p className="text-sm text-gray-600 font-medium mt-1" data-id="element-748">
                      {table.available} available
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Enhanced Booking Sidebar */}
          <div className="lg:col-span-1" data-id="element-749">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-8" data-id="element-750">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-751">
                Make a Reservation
              </h3>
              {/* Booking Type Selection */}
              <div className="mb-6" data-id="element-752">
                <label className="block text-sm font-bold text-gray-900 mb-3" data-id="element-753">
                  Booking Type
                </label>
                <div className="flex flex-col gap-3" data-id="element-754">
                  <button type="button" onClick={() => setBookingType('regular')} className={`p-4 rounded-xl border-2 transition-all text-left ${bookingType === 'regular' ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-755">
                    <div className="flex items-center gap-3" data-id="element-756">
                      <UtensilsIcon className={`w-5 h-5 ${bookingType === 'regular' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-757" />
                      <div className="flex-1" data-id="element-758">
                        <p className={`font-bold text-sm ${bookingType === 'regular' ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-759">
                          Regular Table
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-760">À la carte menu</p>
                      </div>
                    </div>
                  </button>
                  {hasPromotionalPackage && <button type="button" onClick={() => setBookingType('promotional')} className={`p-4 rounded-xl border-2 transition-all text-left relative ${bookingType === 'promotional' ? 'border-red-500 bg-gradient-to-br from-red-50 to-orange-50' : 'border-gray-200 hover:border-red-300'}`} data-id="element-761">
                      <div className="flex items-center gap-3" data-id="element-762">
                        <SparklesIcon className={`w-5 h-5 ${bookingType === 'promotional' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-763" />
                        <div className="flex-1" data-id="element-764">
                          <p className={`font-bold text-sm ${bookingType === 'promotional' ? 'text-red-900' : 'text-gray-900'}`} data-id="element-765">
                            Ramadan Iftar Special
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-766">
                            MVR {restaurant.pricePerPax} per person
                          </p>
                        </div>
                      </div>
                    </button>}
                </div>
              </div>
              {/* Date Selection */}
              <div className="mb-6" data-id="element-767">
                <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-768">
                  Date
                </label>
                <input type="date" value={bookingData.date} onChange={e => setBookingData({
                ...bookingData,
                date: e.target.value
              })} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500" data-id="element-769" />
              </div>
              {/* Number of Guests */}
              <div className="mb-6" data-id="element-770">
                <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-771">
                  Number of Guests
                </label>
                <select value={bookingData.pax} onChange={e => setBookingData({
                ...bookingData,
                pax: parseInt(e.target.value)
              })} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 cursor-pointer" data-id="element-772">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n} data-id="element-773">
                      {n} {n === 1 ? 'Guest' : 'Guests'}
                    </option>)}
                </select>
              </div>
              {/* Time Selection */}
              <div className="mb-6" data-id="element-774">
                <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-775">
                  Preferred Time
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-776">
                  {availableTimeSlots.map(time => <button key={time} type="button" onClick={() => setBookingData({
                  ...bookingData,
                  time: time
                })} className={`px-3 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${bookingData.time === time ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-gray-200 hover:border-amber-300 text-gray-700'}`} data-id="element-777">
                      {time}
                    </button>)}
                </div>
              </div>
              {/* Price Display - Only for promotional */}
              {bookingType === 'promotional' && <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border-2 border-amber-200" data-id="element-778">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-779">Total Price</p>
                  <p className="text-3xl font-bold text-gray-900" data-id="element-780">
                    MVR {restaurant.pricePerPax * bookingData.pax}
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-781">
                    {bookingData.pax} guests × MVR {restaurant.pricePerPax}
                  </p>
                  <p className="text-xs text-gray-600 mt-2" data-id="element-782">
                    + GST & Service Charges
                  </p>
                </div>}
              {/* Availability Info */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6" data-id="element-783">
                <div className="flex items-center gap-3 text-green-800" data-id="element-784">
                  <UsersIcon className="w-6 h-6" data-id="element-785" />
                  <span className="font-bold text-base" data-id="element-786">
                    {totalAvailableSeats} seats available
                  </span>
                </div>
              </div>
              {/* Book Now Button */}
              <button onClick={handleBookNow} disabled={!bookingData.time} className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg mb-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2" data-id="element-787">
                Continue to Booking
                <ArrowRightIcon className="w-5 h-5" data-id="element-788" />
              </button>
              {/* What's Included */}
              <div className="border-t-2 border-gray-200 pt-6" data-id="element-789">
                <h3 className="font-bold text-gray-900 mb-4 text-lg tracking-tight" data-id="element-790">
                  What's Included
                </h3>
                <ul className="space-y-3 text-sm text-gray-600" data-id="element-791">
                  {bookingType === 'promotional' ? <>
                      <li className="flex items-start gap-3" data-id="element-792">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-793">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-794">
                          Full Iftar buffet with unlimited servings
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-795">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-796">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-797">
                          Complimentary dates and water
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-798">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-799">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-800">
                          Traditional beverages included
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-801">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-802">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-803">
                          Entry to daily lucky draw
                        </span>
                      </li>
                    </> : <>
                      <li className="flex items-start gap-3" data-id="element-804">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-805">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-806">
                          Browse full à la carte menu
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-807">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-808">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-809">
                          Pay only for what you order
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-810">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-811">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-812">
                          Table reserved for 2 hours
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-813">
                        <span className="text-green-600 mt-0.5 font-bold text-lg" data-id="element-814">
                          ✓
                        </span>
                        <span className="font-medium leading-relaxed" data-id="element-815">
                          Flexible dining experience
                        </span>
                      </li>
                    </>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-816" />
    </div>;
}